import { isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { FlexiForwardSwitch } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/FlexiForwardSwitch.tsx';
import { getCurrenciesFromCurrencyPair } from '@/App/tiles/FlexiForward/utils.ts';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { flexiForwardCancelRfsInitiate, useCheckFlexiForwardByTradeCaptureMutation } from '@/store/flexi-forward/api';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import { premiumWayChanged } from '@/store/flexi-forward/slice.ts';
import { DEFAULT_PREMIUM_WAY, POSSIBLE_PREMIUM_WAY, type PremiumWay } from '@/store/flexi-forward/state.ts';
import { useAppDispatch } from '@/store/hooks.ts';

export function SolveRateGroup({ tileId }: { tileId: string }): ReactElement {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const [checkFlexiForward] = useCheckFlexiForwardByTradeCaptureMutation();

  const {
    currency1,
    currency2,
    rfsId,
    selectedPremiumWay,
    upfront,
    upfrontCurrency,
    isUpfrontCurrency2,
    tradeCapture,
  } = useFlexiForwardSelector((flexiForward) => {
    const { currency1, currency2 } = getCurrenciesFromCurrencyPair(flexiForward.properties.currencyPair);
    const upfrontCurrency = flexiForward.properties.upfrontCurrency ?? currency1;

    const rfsState = flexiForward.properties.rfs;

    return {
      currency1,
      currency2,
      rfsId: isDefined(rfsState) && 'rfsId' in rfsState ? rfsState.rfsId : undefined,
      selectedPremiumWay: flexiForward.properties.premiumWay ?? DEFAULT_PREMIUM_WAY,
      upfront: flexiForward.properties.upfront ?? undefined,
      upfrontCurrency: upfrontCurrency ?? currency1,
      isUpfrontCurrency2: upfrontCurrency === currency2,
      tradeCapture: flexiForward.tradeCapture,
    };
  });

  const validation = useMemo(
    () => (tradeCapture.status === 'CHECKED' ? tradeCapture?.validation : undefined),
    [tradeCapture],
  );

  function formatPremiumWay(value: PremiumWay): string {
    return formatMessage({ id: `flexiForwardTile.mainFields.premiumWay.toggle.${value}` });
  }

  const handlePremiumWayChanged = useFlexiForwardPropertyChangedHandler<PremiumWay>(
    tileId,
    'Premium way change',
    (premiumWay) => {
      if (isDefined(rfsId)) {
        dispatch(flexiForwardCancelRfsInitiate({ tileId, rfsId }));
      }

      dispatch(premiumWayChanged({ tileId, premiumWay }));

      return { upfront };
    },
  );

  const handleUpfrontCurrencyClicked = useCallback(() => {
    if (isNotDefined(tradeCapture) || isNotDefined(upfront) || upfront === 0) {
      return;
    }

    if (isNotDefined(upfront) || upfront === 0) {
      return;
    }

    if (isDefined(rfsId)) {
      dispatch(flexiForwardCancelRfsInitiate({ tileId, rfsId }));
    }

    const upfrontCurrencyValue = upfrontCurrency === currency1 ? currency2 : currency1;
    sgwtWebAnalytics?.trackEvent('Flexi Forward', 'Upfront currency change', upfrontCurrencyValue);

    checkFlexiForward({
      tileId,
      tradeCapture,
      properties: {
        upfrontCurrency: upfrontCurrencyValue,
      },
    });
  }, [
    tileId,
    rfsId,
    tradeCapture,
    currency1,
    currency2,
    upfront,
    upfrontCurrency,
    checkFlexiForward,
    sgwtWebAnalytics,
  ]);

  const handleUpfrontChanged = useFlexiForwardPropertyChangedHandler<number | undefined>(
    tileId,
    'Upfront changed',
    (upfrontValue) => ({ upfront: isDefined(upfrontValue) ? upfrontValue : null }),
  );

  return (
    <>
      {/* PREMIUM WAY */}
      <FlexiForwardFormField>
        <FlexiForwardSwitch<PremiumWay>
          name="premium-way"
          identifier={tileId}
          values={POSSIBLE_PREMIUM_WAY}
          selectedValue={selectedPremiumWay ?? DEFAULT_PREMIUM_WAY}
          onChange={handlePremiumWayChanged}
          formatValue={formatPremiumWay}
          data-e2e="premium-way-switch"
        />
      </FlexiForwardFormField>

      {/* UPFRONT */}
      <FlexiForwardFormField formattedMessageId="flexiForwardTile.mainFields.upfront.label">
        <TradeCaptureValidationWrapper validation={validation?.upfront}>
          <SgAmountInput
            unit={!isUpfrontCurrency2 ? currency1 : currency2}
            onUnitClick={handleUpfrontCurrencyClicked}
            value={upfront}
            onChange={handleUpfrontChanged}
            size="sm"
            unitBackgroundColor={isUpfrontCurrency2 ? 'warning' : undefined}
            data-e2e="upfront-amount"
          />
        </TradeCaptureValidationWrapper>
      </FlexiForwardFormField>
    </>
  );
}
